import { Box } from "@chakra-ui/react";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from "react";

import { DON, HIT_HARD, HIT_MISSED } from "../lib/taikoGameEmulation";

// The waveform is plotted in the units the TAIKO TEST menu uses, so a
// threshold line sits exactly where its 1..100 setting says it does.
const AXIS_MAX_PERCENT = 100;

// Flagged on a hold the game's frame poll never landed on.
export const MISSED_COLOR = "#dd9b3c";
const LIGHT_RADIUS = 4.5;
const HARD_RADIUS = 9;
const MISSED_RADIUS = 6;

// How near the pointer has to be, in CSS pixels, to take hold of a line.
const GRAB_PX = 9;
const PILL_HEIGHT = 14;
const PILL_MIN_WIDTH = 20;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Canvas height in CSS pixels -> the threshold percentage at that height. */
export const percentAtY = (y, height) =>
    clamp(Math.round((1 - y / height) * AXIS_MAX_PERCENT), 0, AXIS_MAX_PERCENT);

/** Threshold percentage -> its height in CSS pixels. */
export const yAtPercent = (percent, height) =>
    height - (percent / AXIS_MAX_PERCENT) * height;

/**
 * Which threshold line, if either, is close enough to grab at this height.
 * Returns "weak", "strong", or null. When the two sit on top of each other the
 * nearer wins, and a tie goes to the lower line so the upper one stays
 * reachable from above.
 */
export const thresholdFieldAt = (y, height, weak, strong) => {
    if (!height) {
        return null;
    }
    const weakDistance = Math.abs(y - yAtPercent(weak, height));
    const strongDistance = Math.abs(y - yAtPercent(strong, height));
    if (Math.min(weakDistance, strongDistance) > GRAB_PX) {
        return null;
    }
    return weakDistance <= strongDistance ? "weak" : "strong";
};

const traceRoundedRect = (ctx, x, y, width, height, radius) => {
    if (typeof ctx.roundRect === "function") {
        ctx.roundRect(x, y, width, height, radius);
        return;
    }
    ctx.rect(x, y, width, height);
};

/**
 * Canvas oscilloscope for one drum zone.
 *
 * Drawing is driven imperatively from the page's animation loop so the
 * emulator can run at the game's frame rate without pushing a React render per
 * sample. The two dotted lines are the game's small and big note thresholds
 * and can be dragged directly, which keeps them visibly part of the waveform
 * rather than another row of sliders to confuse with the controller's own.
 */
export const DrumScope = forwardRef(({
    channel,
    getWriteIndex,
    color,
    surface,
    bgImage,
    gridColor,
    labelColor,
    surfaceColor,
    thresholdsRef,
    onThresholdChange,
}, ref) => {

    const canvasRef = useRef(null);
    const sizeRef = useRef({ width: 0, height: 0 });
    // Which line the pointer is over or holding. Kept in a ref so hover and
    // drag feedback ride the existing draw loop instead of re-rendering.
    const pointerRef = useRef({ hover: null, drag: null });
    // The latest handler, so the pointer callbacks below can stay stable.
    const changeRef = useRef(onThresholdChange);
    changeRef.current = onThresholdChange;

    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        if (!canvas || !channel) {
            return;
        }
        const { width, height } = sizeRef.current;
        if (width === 0 || height === 0) {
            return;
        }
        const ctx = canvas.getContext("2d");
        const { samples, markers } = channel;
        const windowSize = samples.length;
        const writeIndex = getWriteIndex();
        const { weak, strong } = thresholdsRef.current;
        const { hover, drag } = pointerRef.current;

        const toY = (percent) => height - (percent / AXIS_MAX_PERCENT) * height;
        const toX = (position) => (position / (windowSize - 1)) * width;

        ctx.clearRect(0, 0, width, height);

        // Waveform, oldest sample on the left.
        ctx.beginPath();
        ctx.moveTo(0, height);
        for (let i = 0; i < windowSize; i++) {
            const value = samples[(writeIndex + 1 + i) % windowSize];
            ctx.lineTo(toX(i), toY(value));
        }
        ctx.lineTo(width, height);
        ctx.closePath();
        ctx.fillStyle = `${color}33`;
        ctx.fill();

        ctx.beginPath();
        for (let i = 0; i < windowSize; i++) {
            const value = samples[(writeIndex + 1 + i) % windowSize];
            const x = toX(i);
            const y = toY(value);
            if (i === 0) {
                ctx.moveTo(x, y);
            } else {
                ctx.lineTo(x, y);
            }
        }
        ctx.lineWidth = 1.5;
        ctx.lineJoin = "round";
        ctx.strokeStyle = color;
        ctx.stroke();

        // Threshold handles on top of the trace, so they stay grabbable: the
        // upper one separates a big note from a small one, the lower one is
        // the floor for registering at all.
        const drawHandle = (percent, field) => {
            const active = drag === field || (!drag && hover === field);
            const y = Math.round(toY(percent)) + 0.5;

            ctx.save();
            ctx.setLineDash(active ? [] : [4, 4]);
            ctx.lineWidth = active ? 2 : 1;
            ctx.strokeStyle = active ? color : gridColor;
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(width, y);
            ctx.stroke();
            ctx.restore();

            ctx.save();
            ctx.font = "10px Ubuntu, sans-serif";
            const text = String(percent);
            const pillWidth = Math.max(PILL_MIN_WIDTH, ctx.measureText(text).width + 10);
            const pillY = clamp(y - PILL_HEIGHT / 2, 0, height - PILL_HEIGHT);
            ctx.beginPath();
            traceRoundedRect(ctx, 1, pillY, pillWidth, PILL_HEIGHT, 4);
            ctx.fillStyle = surfaceColor;
            ctx.fill();
            ctx.lineWidth = 1;
            ctx.strokeStyle = active ? color : gridColor;
            ctx.stroke();
            ctx.fillStyle = active ? color : labelColor;
            ctx.textBaseline = "middle";
            ctx.textAlign = "center";
            ctx.fillText(text, 1 + pillWidth / 2, pillY + PILL_HEIGHT / 2);
            ctx.restore();
        };
        drawHandle(strong, "strong");
        drawHandle(weak, "weak");

        // Registered hits, drawn where the game would have taken the sample.
        const filled = surface === DON;
        for (let i = 0; i < windowSize; i++) {
            const slot = (writeIndex + 1 + i) % windowSize;
            const marker = markers[slot];
            if (marker === 0) {
                continue;
            }
            const x = toX(i);
            if (marker === HIT_MISSED) {
                // The hold was strong enough but no frame poll landed on it.
                ctx.save();
                ctx.beginPath();
                ctx.setLineDash([2, 2]);
                ctx.arc(x, toY(weak), MISSED_RADIUS, 0, Math.PI * 2);
                ctx.lineWidth = 1.5;
                ctx.strokeStyle = MISSED_COLOR;
                ctx.stroke();
                ctx.restore();
                continue;
            }
            const radius = marker === HIT_HARD ? HARD_RADIUS : LIGHT_RADIUS;
            const y = Math.max(toY(samples[slot]), radius + 1);
            ctx.beginPath();
            ctx.arc(x, y, radius, 0, Math.PI * 2);
            if (filled) {
                ctx.fillStyle = color;
                ctx.fill();
            } else {
                ctx.lineWidth = 2;
                ctx.strokeStyle = color;
                ctx.stroke();
            }
        }
    }, [channel, getWriteIndex, color, surface, gridColor, labelColor, surfaceColor, thresholdsRef]);

    useImperativeHandle(ref, () => ({ draw }), [draw]);

    /** Pointer position in CSS pixels within the canvas. */
    const localY = useCallback((event) => {
        const canvas = canvasRef.current;
        return event.clientY - canvas.getBoundingClientRect().top;
    }, []);

    const fieldAt = useCallback((y) => {
        const { weak, strong } = thresholdsRef.current;
        return thresholdFieldAt(y, sizeRef.current.height, weak, strong);
    }, [thresholdsRef]);

    const applyCursor = useCallback(() => {
        const canvas = canvasRef.current;
        if (canvas) {
            const { hover, drag } = pointerRef.current;
            canvas.style.cursor = drag || hover ? "ns-resize" : "default";
        }
    }, []);

    const handlePointerDown = useCallback((event) => {
        if (!changeRef.current) {
            return;
        }
        const field = fieldAt(localY(event));
        if (!field) {
            return;
        }
        pointerRef.current.drag = field;
        applyCursor();
        canvasRef.current.setPointerCapture(event.pointerId);
        event.preventDefault();
    }, [fieldAt, localY, applyCursor]);

    const handlePointerMove = useCallback((event) => {
        const y = localY(event);
        const { drag } = pointerRef.current;
        if (drag) {
            // The page owns the legality rules, the same ones the TAIKO TEST
            // menu enforces, so a drag past the other line simply stops there.
            changeRef.current(drag, percentAtY(y, sizeRef.current.height));
            return;
        }
        pointerRef.current.hover = changeRef.current ? fieldAt(y) : null;
        applyCursor();
    }, [fieldAt, localY, applyCursor]);

    const endDrag = useCallback((event) => {
        if (pointerRef.current.drag) {
            pointerRef.current.drag = null;
            const canvas = canvasRef.current;
            if (canvas && canvas.hasPointerCapture(event.pointerId)) {
                canvas.releasePointerCapture(event.pointerId);
            }
            applyCursor();
        }
    }, [applyCursor]);

    const handlePointerLeave = useCallback(() => {
        if (!pointerRef.current.drag) {
            pointerRef.current.hover = null;
            applyCursor();
        }
    }, [applyCursor]);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return undefined;
        }
        const resize = () => {
            const ratio = window.devicePixelRatio || 1;
            const width = canvas.clientWidth;
            const height = canvas.clientHeight;
            canvas.width = Math.max(1, Math.round(width * ratio));
            canvas.height = Math.max(1, Math.round(height * ratio));
            const ctx = canvas.getContext("2d");
            ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            sizeRef.current = { width, height };
            draw();
        };
        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [draw]);

    return (
        <Box
            h="150px"
            borderRadius="md"
            backgroundImage={bgImage}
            backgroundRepeat="no-repeat"
            backgroundSize="72px"
            backgroundPosition="center"
        >
            <canvas
                ref={canvasRef}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onPointerLeave={handlePointerLeave}
                // Without this a touch drag scrolls the page instead.
                style={{ width: "100%", height: "100%", display: "block", touchAction: "none" }}
            />
        </Box>
    );
});

export default DrumScope;
